import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Fade,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
  Description as DescriptionIcon,
  Image as ImageIcon,
  VideoFile as VideoIcon,
  PictureAsPdf as PdfIcon,
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
} from "@mui/icons-material";
import PDFViewer from "./PDFViewer";
import HTMLViewer from "./HTMLViewer";
import VideoViewer from "./VideoViewer";
import {
  isPdfFile,
  isVideoFile,
  isPreviewableAsPdf,
  isPreviewableAsHtml,
  isImageFile,
} from "../../hooks/useFileOperations";

export const ThumbnailPreview = ({
  selectedFile,
  thumbnailPreviewOpen,
  onClose,
  thumbnails,
  singleThumbnails,
  temporaryLinks,
  previews,
  temporaryLinkLoading,
  previewLoading,
  thumbnailsLoading,
  currentFolder,
  onNavigateToImage,
}: any) => {
  if (!selectedFile) return null;

  const largeThumbnail = singleThumbnails[selectedFile.path];
  const smallThumbnail = thumbnails[selectedFile.path];
  const temporaryLinkData = temporaryLinks[selectedFile.path];
  const previewData = previews[selectedFile.path];
  const isLoadingContent =
    thumbnailsLoading || temporaryLinkLoading || previewLoading;
  const isPdf = isPdfFile(selectedFile.name);
  const isVideo = isVideoFile(selectedFile.name);
  const isPreviewPdf = isPreviewableAsPdf(selectedFile.name);
  const isPreviewHtml = isPreviewableAsHtml(selectedFile.name);
  const isImage = isImageFile(selectedFile.name);

  // Get all images from current folder for navigation
  const allImages =
    currentFolder?.children?.filter(
      (child: any) => child.type === "file" && isImageFile(child.name)
    ) || [];

  const currentImageIndex = allImages.findIndex(
    (img: any) => img.path === selectedFile.path
  );
  const hasPrevious = isImage && currentImageIndex > 0;
  const hasNext = isImage && currentImageIndex < allImages.length - 1;

  const handlePrevious = () => {
    if (hasPrevious && onNavigateToImage) {
      onNavigateToImage(allImages[currentImageIndex - 1]);
    }
  };

  const handleNext = () => {
    if (hasNext && onNavigateToImage) {
      onNavigateToImage(allImages[currentImageIndex + 1]);
    }
  };

  const renderPreviewContent = () => {
    if (isLoadingContent) {
      return (
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress size={24} />
          <Typography variant="body2" sx={{ mt: 2 }}>
            {isPdf
              ? "Loading PDF..."
              : isVideo
                ? "Loading video..."
                : isPreviewPdf
                  ? "Loading document..."
                  : isPreviewHtml
                    ? "Loading spreadsheet..."
                    : "Loading preview..."}
          </Typography>
        </Box>
      );
    }

    if (isPdf && temporaryLinkData) {
      return <PDFViewer url={temporaryLinkData} selectedFile={selectedFile} />;
    }

    if (isVideo && temporaryLinkData) {
      return (
        <VideoViewer url={temporaryLinkData} selectedFile={selectedFile} />
      );
    }

    if ((isPdf || isVideo) && !temporaryLinkData) {
      return (
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress size={24} />
          <Typography variant="body2" sx={{ mt: 2 }}>
            {isPdf ? "Preparing PDF..." : "Preparing video..."}
          </Typography>
        </Box>
      );
    }

    if (isPreviewPdf && previewData?.preview) {
      return (
        <PDFViewer
          data={previewData.preview}
          isPreview={true}
          selectedFile={selectedFile}
        />
      );
    }

    if (isPreviewPdf && !previewData?.preview) {
      return (
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress size={24} />
          <Typography variant="body2" sx={{ mt: 2 }}>
            Preparing document preview...
          </Typography>
        </Box>
      );
    }

    if (isPreviewHtml && previewData?.preview) {
      return (
        <HTMLViewer
          htmlData={previewData.preview}
          selectedFile={selectedFile}
        />
      );
    }

    if (isPreviewHtml && !previewData?.preview) {
      return (
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress size={24} />
          <Typography variant="body2" sx={{ mt: 2 }}>
            Preparing spreadsheet preview...
          </Typography>
        </Box>
      );
    }

    // ── Image rendering: prefer large thumbnail, fall back to small thumbnail,
    //     then to temporary link (full-res), finally show error ──────────────
    const safeLargeThumbnail =
      typeof largeThumbnail === "string" && largeThumbnail.length > 0
        ? largeThumbnail
        : null;
    const safeSmallThumbnail =
      typeof smallThumbnail === "string" && smallThumbnail.length > 0
        ? smallThumbnail
        : null;

    if (safeLargeThumbnail) {
      return (
        <img
          src={`data:image/jpeg;base64,${safeLargeThumbnail}`}
          alt={selectedFile.name}
          style={{
            maxWidth: "100%",
            maxHeight: "100%",
            objectFit: "contain",
            borderRadius: 0,
            display: "block",
          }}
          onError={(e) => {
            // If the data URI fails to render, hide it so fallback shows
            (e.target as HTMLImageElement).style.display = "none";
          }}
        />
      );
    }

    if (safeSmallThumbnail) {
      return (
        <img
          src={`data:image/jpeg;base64,${safeSmallThumbnail}`}
          alt={selectedFile.name}
          style={{
            maxWidth: "100%",
            maxHeight: "100%",
            objectFit: "contain",
            borderRadius: 0,
            display: "block",
          }}
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = "none";
          }}
        />
      );
    }

    // Fallback: use the full-resolution temporary link for images
    if (isImage && temporaryLinkData) {
      return (
        <img
          src={temporaryLinkData}
          alt={selectedFile.name}
          style={{
            maxWidth: "100%",
            maxHeight: "100%",
            objectFit: "contain",
            borderRadius: 0,
            display: "block",
          }}
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = "none";
          }}
        />
      );
    }

    // Still loading temporary link for image
    if (isImage && !temporaryLinkData && temporaryLinkLoading) {
      return (
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress size={24} />
          <Typography variant="body2" sx={{ mt: 2 }}>
            Loading full image…
          </Typography>
        </Box>
      );
    }

    // No preview available
    return (
      <Box sx={{ textAlign: "center", color: "text.secondary" }}>
        {isPdf || isPreviewPdf ? (
          <>
            <PdfIcon sx={{ fontSize: 48, mb: 1 }} />
            <Typography variant="body2">
              {isPdf
                ? "PDF preview not available"
                : "Document preview not available"}
            </Typography>
          </>
        ) : isVideo ? (
          <>
            <VideoIcon sx={{ fontSize: 48, mb: 1 }} />
            <Typography variant="body2">Video preview not available</Typography>
          </>
        ) : isPreviewHtml ? (
          <>
            <DescriptionIcon sx={{ fontSize: 48, mb: 1 }} />
            <Typography variant="body2">
              Spreadsheet preview not available
            </Typography>
          </>
        ) : (
          <>
            <ImageIcon sx={{ fontSize: 48, mb: 1 }} />
            <Typography variant="body2">
              Image could not be loaded. The file may be unsupported or temporarily unavailable.
            </Typography>
          </>
        )}
      </Box>
    );
  };

  return (
    <Fade in={thumbnailPreviewOpen}>
      <Box
        sx={{
          borderRadius: 0,
          display: "flex",
          flexDirection: "column",
          height: "100%",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton onClick={onClose} size="small">
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" component="h2">
              {selectedFile.name}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {/* Navigation buttons - only for images */}
            {isImage && allImages.length > 1 && (
              <>
                <IconButton
                  onClick={handlePrevious}
                  disabled={!hasPrevious}
                  size="small"
                >
                  <NavigateBeforeIcon />
                </IconButton>
                <Typography
                  variant="body2"
                  sx={{ mx: 1, color: "text.secondary" }}
                >
                  {currentImageIndex + 1} / {allImages.length}
                </Typography>
                <IconButton
                  onClick={handleNext}
                  disabled={!hasNext}
                  size="small"
                >
                  <NavigateNextIcon />
                </IconButton>
              </>
            )}

            <IconButton onClick={onClose} edge="end">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Content Display */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            p: 2,
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          {renderPreviewContent()}
        </Box>
      </Box>
    </Fade>
  );
};

export default ThumbnailPreview;
